import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center gap-3">
          <Shield className="h-8 w-8 text-primary-500" />
          <h1 className="text-3xl font-bold text-white">Privacy Policy</h1>
        </div>
        
        <div className="bg-zinc-900 rounded-lg p-8 mb-8">
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">Last Updated: July 1, 2025</p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">1. Introduction</h2>
            <p className="text-gray-300">
              Jacky Circuit respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and safeguard your information when you use our website and services, including our track booking system, event registration platform, and driver profile management.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">2. Information We Collect</h2>
            <p className="text-gray-300">We collect the following types of information:</p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>Account Information:</strong> When you register, we collect your name, email address, phone number, and password.</li>
              <li><strong>Profile Information:</strong> For driver profiles, we collect personal details, vehicle information, and driver's license information.</li>
              <li><strong>Promotor Information:</strong> For promotor accounts, we collect company name, CRIB number, and KVK number.</li>
              <li><strong>Booking Information:</strong> When you make a booking, we collect date, time, track type, duration, and payment information.</li>
              <li><strong>Event Registration Information:</strong> When you register for events, we collect category preferences and vehicle details.</li>
              <li><strong>Payment Information:</strong> We collect payment status information but do not store credit card details.</li>
              <li><strong>Usage Information:</strong> We collect information about how you interact with our website, including IP address, browser type, and pages visited.</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-300">We use your information for the following purposes:</p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li>To create and manage your account</li>
              <li>To process and confirm track bookings</li>
              <li>To register you for racing events</li>
              <li>To verify driver and vehicle information for safety purposes</li>
              <li>To process payments and security deposits</li>
              <li>To send booking confirmations and event notifications</li>
              <li>To communicate important updates about our services</li>
              <li>To improve our website and services</li>
              <li>To comply with legal obligations</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">4. Legal Basis for Processing</h2>
            <p className="text-gray-300">
              We process your personal data based on the following legal grounds:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>Contract:</strong> Processing is necessary for the performance of our contract with you (booking agreements, event registrations).</li>
              <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate interests, such as improving our services and ensuring track safety.</li>
              <li><strong>Legal Obligation:</strong> Processing is necessary to comply with legal requirements.</li>
              <li><strong>Consent:</strong> Where required by law, we will obtain your explicit consent.</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-300">
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>Service Providers:</strong> Third-party vendors who help us operate our business (payment processors, email service providers).</li>
              <li><strong>Event Organizers:</strong> For event registrations, necessary information may be shared with event organizers.</li>
              <li><strong>Legal Authorities:</strong> When required by law, court order, or governmental regulation.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
            </ul>
            <p className="text-gray-300 mt-2">
              We do not sell your personal information to third parties.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">6. Data Security</h2>
            <p className="text-gray-300">
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li>Encryption of sensitive data</li>
              <li>Secure storage using industry-standard practices</li>
              <li>Access controls and authentication procedures</li>
              <li>Regular security assessments</li>
            </ul>
            <p className="text-gray-300 mt-2">
              While we strive to protect your personal information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">7. Data Retention</h2>
            <p className="text-gray-300">
              We retain your personal data for as long as necessary to fulfill the purposes for which we collected it, including for the purposes of satisfying any legal, accounting, or reporting requirements. For track bookings and event registrations, we typically retain data for up to 7 years for tax and accounting purposes.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">8. Your Rights</h2>
            <p className="text-gray-300">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>Access:</strong> The right to request copies of your personal data.</li>
              <li><strong>Rectification:</strong> The right to request that we correct inaccurate or incomplete information.</li>
              <li><strong>Erasure:</strong> The right to request that we delete your personal data in certain circumstances.</li>
              <li><strong>Restriction:</strong> The right to request that we restrict the processing of your data in certain circumstances.</li>
              <li><strong>Data Portability:</strong> The right to request that we transfer your data to another organization or directly to you.</li>
              <li><strong>Objection:</strong> The right to object to processing of your personal data.</li>
            </ul>
            <p className="text-gray-300 mt-2">
              To exercise any of these rights, please contact us at info@jackycircuit.com.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">9. Cookies and Similar Technologies</h2>
            <p className="text-gray-300">
              Our website uses cookies and similar technologies to enhance your browsing experience, analyze website traffic, and personalize content. You can control cookies through your browser settings.
            </p>
            <p className="text-gray-300 mt-2">
              We use the following types of cookies:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-2">
              <li><strong>Essential Cookies:</strong> Required for the website to function properly.</li>
              <li><strong>Analytical Cookies:</strong> Help us understand how visitors interact with our website.</li>
              <li><strong>Functional Cookies:</strong> Enable enhanced functionality and personalization.</li>
            </ul>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-300">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">11. International Data Transfers</h2>
            <p className="text-gray-300">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. When we transfer your information, we will take appropriate measures to protect it.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-gray-300">
              We may update this privacy policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will notify you of any material changes by posting the new policy on our website and updating the "Last Updated" date.
            </p>
            
            <h2 className="text-xl font-semibold text-white mt-6 mb-4">13. Contact Us</h2>
            <p className="text-gray-300">
              If you have any questions about this privacy policy or our data practices, please contact us at:
            </p>
            <p className="text-gray-300 mt-2">
              Email: info@jackycircuit.com<br />
              Phone: +1 (555) 123-4567<br />
              Address: Willemhoyerstraat 60, Curaçao
            </p>
          </div>
        </div>
        
        <div className="flex justify-center mb-8">
          <Link to="/" className="flex items-center gap-2 text-primary-500 hover:text-primary-400">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}